import { FormValues } from '../../duck';
import { Button, Flex, Text } from '@chakra-ui/react';
import { isEmpty } from 'lodash-es';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { TextInput } from '@lon/mathnation-suit/components';
import { useQueryParams } from '@lon/mathnation-suit/hooks';
import { Asterisk, FormSelect } from '@lon/shared/components';
import { useGetPreviewDistrictsQuery } from '@lon/shared/requests';

const PasswordForm: React.FC<{ onForgot(): void }> = ({ onForgot }) => {
  const navigate = useNavigate();
  const [queryParams, setParams] = useQueryParams();
  const { t } = useTranslation();
  const params = useParams();
  const { isSubmitting } = useFormState();

  const { setValue } = useFormContext<FormValues>();

  const { data, loading } = useGetPreviewDistrictsQuery({
    variables: {
      itemsPerPage: 999,
      page: 1,
    },
  });

  const districtOptions = useMemo(() => {
    if (!data?.previewAccountDistricts?.collection?.length) {
      return [];
    }

    return (
      data?.previewAccountDistricts?.collection.map((v) => ({
        value: v?.id ?? '',
        label: v?.globalName ?? '',
      })) || []
    );
  }, [data?.previewAccountDistricts?.collection]);

  useEffect(() => {
    const currentPreviewDistrict =
      data?.previewAccountDistricts?.collection?.find(
        (district) => district?.urlCode === params.urlCode
      );

    if (!isEmpty(currentPreviewDistrict)) {
      setValue('district', currentPreviewDistrict?.id, { shouldDirty: true });
      changeDistrict({ value: currentPreviewDistrict?.id });
    }
  }, [data?.previewAccountDistricts?.collection]);

  const changeDistrict = (data: any) => {
    setParams({ ...queryParams, state: null, district: data.value });
  };

  const handleReset = () => {
    setParams({ ...queryParams, state: null, district: null });
    navigate('/mathnation/login/preview');
  };

  return (
    <>
      <Text as="h2" fontSize="lg" fontWeight="bold" mb={6}>
        Welcome! Please log in to your account.
      </Text>

      <FormSelect
        disabled={!!params.urlCode}
        onChange={changeDistrict}
        isRequired
        name="district"
        label={t('previewLogin.districtLabel')}
        placeholder={t(`previewLogin.${'districtPlaceholderMobile'}`)}
        options={districtOptions}
        variant="dark"
        isLoading={loading}
        aria-label={t('previewLogin.districtLabel')}
        formLabelProps={{
          requiredIndicator: <Asterisk color="var(--orange-5)" />,
        }}
      />

      <TextInput
        isRequired
        name="username"
        label="Username"
        placeholder="Enter your username"
        inputLayoutProps={{ mt: '4' }}
      />
      <TextInput
        isRequired
        type="password"
        name="password"
        label="Password"
        placeholder="Enter your password"
        inputLayoutProps={{ mt: '4' }}
      />

      <Flex mt={4} justifyContent="space-between">
        <Button
          variant="link"
          colorScheme="blue"
          _hover={{ textDecoration: 'none !important' }}
          onClick={handleReset}
          isDisabled={!params.urlCode}
        >
          Reset District
        </Button>

        <Flex gridGap={4}>
          <Button
            colorScheme="blue"
            type="submit"
            isLoading={isSubmitting}
            variant="solid"
          >
            Log In
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

export default PasswordForm;
